import { pick } from "lodash";
import React from "react";
import ReactDOM from "react-dom";
import { EDITOR_ROLES } from "@app/types";
import Button from "../form_components/Button";
import Input from "../form_components/Input";
import { SubmitPrompts } from "../page-components/modals";
import SaveToRepository from "../utilities/SaveToRepository";
import { createDesignChangeMap } from "./../utilities/versioning";
import { MergeDialog } from "./dialogs/MergeDialog";
import { ReviewContainer } from './components/versioning/ReviewContainer';
import { ReactDOMComponents } from "../utilities/ReactDOMComponents";
import Utility from './../utilities/Utility'; 
import BranchingMergingUpgrade from './components/versioning/BranchingMergingUpgrade';
import NonCompatibleFeaturesDialog from "./dialogs/NonCompatibleFeaturesDialog";
import DesignerSetting from "../DesignerSetting";
import { defaultServiceContext } from "../contexts";
import { WorkflowPromptStatuses } from "../page-components";
import { hasLocalChanges } from "../utilities";
import ExportWidget from "./ExportWidget";

const DESIGN_VERSION_ERROR_TITLE = 'Invalid Design Version';
const DESIGN_VERSION_ERROR_MESSAGE = 'Please enter a three-part version number such as 1.0.0';
const {
    avsService,
    errorMessageService,
    loadingService,
    userService
} = defaultServiceContext;

export default class SaveWidget extends React.Component {
    /**
     * Rendered instance of the @see SaveWidget component.
     * 
     * TODO: Remove after decoupling saving logic from this component and adding it to a service.
     */
    static instance = null;

    /**
     * Export page
     * @module SaveWidget
     * @param {Object} props React props
     */
    constructor(props) {
        super(props);

        SaveWidget.instance = this;

        this.state = {
            repoVersion: this.props.serialized.repoVersion,
            designRoles: userService.designRoles.value,
            designVersion: this.props.serialized.repoVersion,
            designVersionHasErrors: false,
            saveAsNewVersion: false,
            isPromptStatusesOpen: false,
            isSaving: false,
            isSubmitPromptsOpen: false,
            latestAndPreviousVersionsReady: false,
            validateOnSave: false,
            selectedSourceVersion: "",
            selectedTargetVersion: ""
        };

        this.handleNewDesignVersion = this.handleNewDesignVersion.bind(this);
        this.onSaveRelease = this.onSaveRelease.bind(this);
        this.validateInputRepoVersion = this.validateInputRepoVersion.bind(this);
        this.handleValidateCheckbox = this.handleValidateCheckbox.bind(this);
        this.handleSaveAsNewVersion = this.handleSaveAsNewVersion.bind(this);
        this.downloadChangeLogMaps = this.downloadChangeLogMaps.bind(this);
        this.reactDOMComponents = new ReactDOMComponents();
        this.saveDesign = this.saveDesign.bind(this)
        this.getLatestApplication = this.getLatestApplication.bind(this)
        this.saveButtonDisabled = this.saveButtonDisabled.bind(this);
        this._onDesignRolesChange = this._onDesignRolesChange.bind(this);

        this.latestAndPreviousVersionsTimer = null;
    }

    setConfig(key, value) {
        window.apm.setBasic(key, value);
        if(key === 'applicationVersion') {
            window.apm.setBasic('applicationVersionLastChange', window.apm.timestamp)
        }

    }

    getConfig(key) {
        var basicPrefix = "basic-";
        if (key.startsWith(basicPrefix)) {
            return this.props.serialized[key.substring(basicPrefix.length, key.length)];
        }
        return this.props.serialized[key];
    }

    async onSaveRelease() {
        const unsavedChanges = loadingService.useLoading(
            () => { return hasLocalChanges(); },
            "Checking for Changes..."
        );
        if (unsavedChanges) {
            errorMessageService.addError(`${window.l.notification.unsaved_changes} release.`);
            return;
        }

        await loadingService.useLoading(
            async () => {
                this.setState({ isSaving: true });
                let {
                    latestSerialized,
                    serialized,
                    timestamp
                } = window.apm;
                serialized = {
                    ...serialized,
                    applicationVersion: serialized.applicationVersion.replace("-SNAPSHOT", ""),
                    applicationVersionLastChange: timestamp
                };
                const changeMap = createDesignChangeMap(
                    serialized,
                    latestSerialized
                );
                const savedSerialized = await window.colm.saveWithCollaboration({
                    changeLogMap: [changeMap, {}],
                    commitMessage: "Create release version.",
                    mergedVersion: serialized
                });

                if (savedSerialized) {
                    await avsService.createTag({
                        ...pick(serialized, [
                            "branchId",
                            "version"
                        ]),
                        tag: "production ready"
                    });
                    this.setState({ isSubmitPromptsOpen: true });
                }

                this.setState({ isSaving: false });
            },
            "Saving to Repository..."
        );
    }

    componentDidMount() {
        this.checkLatestAndPreviousVersionsReady();
        userService.designRoles.subscribe(this._onDesignRolesChange);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.serialized.repoVersion !== this.state.repoVersion) {
            this.setState({ repoVersion: this.props.serialized.repoVersion });
            this.setState({ designVersion: this.props.serialized.repoVersion });
        }
    }

    componentWillUnmount() {
        clearTimeout(this.latestAndPreviousVersionsTimer);
        userService.designRoles.unsubscribe(this._onDesignRolesChange);
    }

    // commenting out, users asked to not make the validation checkbox control 'sticky'
    // componentWillReceiveProps(nextProps) {
    //     if (!this.state.validateOnSave) {
    //         this.setState({ validateOnSave: true })
    //     }
    // }

    /**
     * validateOnSave toggles validation popup on save
     */
    handleValidateCheckbox() {
        this.setState({ validateOnSave: !this.state.validateOnSave })
    }

    /**
     * 
     * @param {event} e 
     */
    handleNewDesignVersion(e){
        if(!this.state.saveAsNewVersion){
            return;
        }
        this.setState({designVersion: e.target.value})
    }

    /**
     * Validation for design version input
     * @param {event} e 
     */
    validateInputRepoVersion(e) {
        const val = e.target.value;
        // allow for either 1.2.3 or 1.2.3.4 versioning schemes
        const re = new RegExp(/^[0-9]+(\.[0-9]+){2,3}$/);
        const errorContainer = $("#error-input-design-version");

        if(re.test(val)){
            this.setState({designVersion: val});
            this.setState({designVersionHasErrors: false});
            errorContainer.hide().removeClass("error");
        }
        else {
            this.setState({designVersionHasErrors: true});
            errorContainer.show().addClass("error").animateCss("shake");
        }
    }

    
    /**
     * Handles save as new version checkbox and controls saveAsNewVersion state
     * @param {event} e 
     */
    handleSaveAsNewVersion(e){
        this.setState({saveAsNewVersion: !this.state.saveAsNewVersion});
        // reset to repoVersion if user deselects this checkbox
        if(!e.target.checked){
            const errorContainer = $("#error-input-design-version");
            this.setState({designVersion: this.props.serialized.repoVersion});
            this.setState({designVersionHasErrors: false});
            errorContainer.hide().removeClass("error");            
        }
    }

    /**
     * 
     */
    getToggleValidateCheckbox() {
        return (
            <div style={{width: "127px", marginLeft:"auto"}}>
                <input type="checkbox" 
                    id={"toggleValidateOnSave"} 
                    checked={this.state.validateOnSave} 
                    onChange={this.handleValidateCheckbox} 
                />
                <span style={{ paddingLeft: '10px' }}>Validate on Save</span>
            </div>
        );
    }

    /**
     * Is user is trying to save a Non Omni Channel version into an OmniChannel version 
     * @param {*} currentVersion 
     * @param {*} latestVersion 
     * @returns 
     */
     getIsSavingNonOmniChannelIntoOmniChannel(currentVersion, latestVersion){
        const latestHasOmniChannel = latestVersion.omniChannel?.isOmniChannel;
        const currentHasOmniChannel = currentVersion.omniChannel?.isOmniChannel;

        return latestHasOmniChannel && !currentHasOmniChannel;
    }

    /**
     * Is user is trying to save a Non Branching Merging version into a Branching Merging version
     * @param {*} currentVersion 
     * @param {*} latestVersion 
     * @returns boolean
     */
    getIsSavingNonBranchingMergingIntoBranchingMerging(currentVersion, latestVersion){
        const latestHasBranchingMerging = latestVersion.branchingMerging?.initialized;
        const currentHasBranchingMerging = currentVersion.branchingMerging?.initialized;

        return latestHasBranchingMerging && !currentHasBranchingMerging;
    }

    /**
     * Opens a modal that informs users that their design version cannot be saved into latest version due to non-backwards compatibility
     * @param {*} nonCompatibleFeatures 
     * @param {object} latestVersion   
     */
    handleIsNonCompatibleFeatures(nonCompatibleFeatures = [], {client, application, version}){
        const nonCompatibilityHeader = `This version of ${client} ${application} is no longer compatible with the latest version (${version})`;

        window.apm.popup(
            `<div>
                <i class="fe icon-alert-triangle" style="font-size: 1.5em"/>
                <span style="padding-left: 6px">
                    Version no longer compatible
                </span>
            </div>`,
            '<div class="version_not_compatible_dialog_container"></div>',
            `<button class="w2ui-btn primary  close-button">Close</button>`,
            [
                ".version_not_compatible_dialog_container",

                () => {
                    ReactDOM.render(
                        <NonCompatibleFeaturesDialog 
                            nonCompatibilityHeader = {nonCompatibilityHeader}
                            nonCompatibleFeatures = {nonCompatibleFeatures}
                        />
                        ,
                        $(".version_not_compatible_dialog_container").get(0));
                        $(".w2ui-popup").find(".close-button").on("click", () => {
                            // reset isSaving state and tear down popup components
                            this.setState({ isSaving: false });
                            ReactDOM.unmountComponentAtNode($(".version_not_compatible_dialog_container").get(0));
                            w2popup.close();
                        });

                }
            ],
            250, // height
            null, // node
            null, // treenode
            false, // showClose
            650
        );
    }

    /**
     * Returns array of non-backwards compatible features
     * @param {*} serialized 
     * @param {*} latestSerialized 
     * @returns array of non-backwards compatible features
     */
    getNonCompatibleFeatures(serialized, latestSerialized){
        let nonCompatibleFeatures = [];
        
        if(this.getIsSavingNonOmniChannelIntoOmniChannel(serialized, latestSerialized)){
            nonCompatibleFeatures.push("Omni Channel");
        };
        
        if(this.getIsSavingNonBranchingMergingIntoBranchingMerging(serialized, latestSerialized)){
            nonCompatibleFeatures.push("Branching and Merging");
        };
        
        return nonCompatibleFeatures;
    }

    /**
     * Save Dialog renders merge dialog, saving status, etc. 
     * Handles new applications, collaboration and non-collaboration saves
     */
     async saveDesign(clonedSerialized = null) {
        const serialized = clonedSerialized || window.apm.serialized;
        const { branchId } = serialized;
        const { latestSerialized } = window.apm;
        const isNonCollab = Utility.getIsNonCollaboration();
        const nonCompatibleFeatures = this.getNonCompatibleFeatures(serialized, latestSerialized);
        if (!isNonCollab && nonCompatibleFeatures.length > 0) {
            this.setState({ isSaving: false });
            this.handleIsNonCompatibleFeatures(nonCompatibleFeatures, latestSerialized);
            return;
        }

        const latestVersion = await avsService.getLatestVersion(branchId);
        const isNewApplication = !latestVersion;
        const message = (isNonCollab || isNewApplication) ? 'Saving changes...' : 'Generating change logs...';
        const loadingId = loadingService.addMessage(message);

        const setDesignVersion = () => {
            const { designVersion, saveAsNewVersion } = this.state;
            if (saveAsNewVersion) {
                serialized.version = designVersion;
                serialized.repoVersion = designVersion;
            }
        };
        const onComplete = () => {
            loadingService.removeMessage(loadingId);
            this.setState({
                isSaving: false,
                saveAsNewVersion: false
            });
        };

        // New application save.
        if (isNewApplication) {
            setDesignVersion();
            await this.saveNewApp(serialized);
            onComplete();
            return;
        }

        // No collaboration save.
        if (isNonCollab) {
            setDesignVersion();
            await this.saveWithoutCollaboration(serialized);
            onComplete();
            return;
        }

        // Collaboration save.
        const {
            designVersion: newVersionNumber,
            saveAsNewVersion
        } = this.state;

        MergeDialog.open({
            ...await avsService.getDocumentsForSave(serialized),
            newVersionNumber: (saveAsNewVersion) ? newVersionNumber : null,
            onComplete
        });
        loadingService.removeMessage(loadingId);
    }

    /**
     * Initial save to AVS
     * @param {object} serialized 
     */
    async saveNewApp(serialized) { 
        const changeLog = [window.apm.getChangeLogEntry(
            {
                author: window.apm.getDesignerAuthor(),
                latestVersion: serialized.version,
                initial: true,
                commitMessage: "Initial commit"
            }
        )];

        const newApp = await SaveToRepository.saveToRepoDesign(serialized, changeLog);
        if (newApp == null) {
            return;
        }

        const { version } = newApp;
        if (version) {
            this.setState({ repoVersion: version });

            window.apm.serialized = newApp;
            window.apm.setBasic("repoVersion", version);
            window.apm.checkNotifyVersion();
            window.apm.setSerializedChanged();

            this.reactDOMComponents.renderApplicationPages(newApp);

            window.apm.postEventToAllViews({ target: "request-update-member" });
        } else {
            console.error('saveToRepository did not return a version');
        }
    }

    /**
     * Refresh application UI data with a newly saved app version (serialized)
     * @param {object} serialized 
     */
    refreshApplicationUIAfterSave(serialized){
        const {version } = serialized;
        this.setState({ repoVersion: version });
        this.setState({ designVersion: version });
        this.setState({ isSaving: false });

        window.apm.setBasic("repoVersion", version);
        window.apm.serialized = serialized;
        window.apm.checkNotifyVersion();
        window.apm.setSerializedChanged();

        this.reactDOMComponents.renderApplicationPages(serialized);

        // refresh app data for canvas
        window.apm.postEventToCurrentView({ target: "request-reload-data-model" });
        // update unit tests
        document.dispatchEvent(new CustomEvent("ev-unit-test-change"));
        // update sample dialogs
        document.dispatchEvent(new CustomEvent("ev-dialog-changed"));

        // refresh the APPLICATION PANEL
        window.apm.refreshApplicationTree();

        // refresh the CURRENT DIAGRAM 
        // and close all opened property panels as they cannot be updated after save
        window.apm.postEventToCurrentView({ target: "request-update-member", commands: {hidePropertyPanel: true} });

        $('#app-title').html(`${this.props.serialized.clientName} ${this.props.serialized.designName}`);
        $('#app-version').html(this.props.serialized.version);
    }

    /**
     * Save to AVS without Collaboration
     * @param {object} serialized 
     */
    async saveWithoutCollaboration(serialized) {
        const savedApp = await SaveToRepository.saveToRepoDesign(serialized, null);
        if (savedApp == null) {
            return;
        }

        const { version, repoVersion } = savedApp;
        if (version) {
            this.setState({ repoVersion: version });
            this.setState({ designVersion: repoVersion });

            window.apm.serialized = savedApp;
            window.apm.setBasic("repoVersion", version);
            window.apm.checkNotifyVersion();
            window.apm.setSerializedChanged();

            $('#app-title').html(savedApp.clientName + " " + savedApp.designName);
            $('#app-version').html(version);

            this.reactDOMComponents.renderApplicationPages(savedApp);

            // refresh canvas and trees and settings
            window.apm.postEventToAllViews({ target: "request-reload-data-model" });
            // update property panels
            document.dispatchEvent(new CustomEvent("ev-property-refresh"));
        }
    }

    /**
     * Returns the lastest version of application 
     * @param {string} client 
     * @param {string} application 
     * @param {string} version 
     * @returns 
     */
    async getLatestApplication(version){
        const { branchId } = window.apm.serialized;
        const application = await avsService.getDesign(branchId, version);
        window.apm.checkNotifyVersion();
        return application;
    }

    getValidationDialog() {
        window.apm.popup(
            window.l.popup.validator_results,
            '<div class="validator_container"></div>',
            '<button class="w2ui-btn primary add-button">' + window.l.popup.save + '</button> ' +
            '<button class="w2ui-btn secondary">' + window.l.popup.close + '</button>',
            [
                ".validator_container",
                () => {
                    const dialogContainer = document.querySelector('.validator_container');
                    const closeDialog = () => {
                        this.reactDOMComponents.unmountComponentAtNode(dialogContainer);
                        w2popup.close();
                    };
                    const cancelSave = () => {
                        this.setState({ isSaving: false });
                        closeDialog();
                    };

                    ReactDOM.render(
                        <ValidatorDialog closeDialog={cancelSave} />,
                        dialogContainer
                    );

                    $(".w2ui-popup").find(".w2ui-btn.secondary").on("click", cancelSave);

                    $(".w2ui-popup").find(".add-button").on("click", () => {
                        closeDialog();
                        this.saveDesign();
                    });
                }
            ],
            700, // height
            null, // node
            null, // treenode
            false // showClose
        );
    }

    downloadChangeLogMaps() {
        const applicationObj = {
            client: this.props.serialized.client,
            app: this.props.serialized.application,
            version: window.apm.latestVersion
        };
        window.colm.downLoadLatestChangeLogMap(applicationObj)
    }


    //to check if the module used in GlobalException handling exista in app or not
    moduleOptions() {
        const modules = window.apm.knowledgeBase.getModules();
        return modules.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
    }
    /**
        * validate Exception handling
        * @param {*} serialized 
        * @returns 
    */
    validateGlobalExceptionHandling(serialized){
        const errorSel = window.apm.knowledgeBase.getIndexFromValue(serialized.globalExceptionHandling.exception, this.moduleOptions()); 
        const timeoutSel = window.apm.knowledgeBase.getIndexFromValue(serialized.globalExceptionHandling.timeout, this.moduleOptions())

        if(errorSel === -1){
          return 'Process engine exception';
        }
        if(timeoutSel === -1){
            return 'Timeout Handler';
        }
    }

    /**
     * Starts a timer to check if both latest and previous versions of the current design
     * are ready/loaded or not. Both are needed in order to build the changelog UI.
     */
    checkLatestAndPreviousVersionsReady() {
        if (!!window.apm.latestSerialized && !!window.apm.latestVersion) {
            this.setState({ latestAndPreviousVersionsReady: true });
            clearTimeout(this.latestAndPreviousVersionsTimer);
            this.latestAndPreviousVersionsTimer = null;
        } else {
            this.latestAndPreviousVersionsTimer = setTimeout(() => {
                this.checkLatestAndPreviousVersionsReady();
            }, 1500);
        }
    }

    /**
     * Determines whether the Save button should be disabled or not.
     */
    saveButtonDisabled() {
        const newVersionClicked = DesignerSetting.s.is_collab === "false" ? this.state.saveAsNewVersion : true;
        return !this.state.latestAndPreviousVersionsReady || this.state.isSaving || !newVersionClicked;
    }

    render() {
        const {
            designRoles,
            isPromptStatusesOpen,
            isSubmitPromptsOpen
        } = this.state;
        const isInteractionsCommon = Utility.getIsCommonModule(this.props.serialized);
        const isDesigner = userService.hasDesignRole(designRoles, EDITOR_ROLES);

        return (
            <div className="container-fluid" style={{ marginTop: '3em' }}>
                <div className="col-md-8 col-md-offset-2">
                    <div id="versioning-export" className="">
                        {/* Versioning */}
                        <div className="row">
                            <div className="col-md-12">
                                <h4>Versioning</h4>
                            </div>
                            <div className="col-md-6">
                                <Input 
                                    disabled={true} 
                                    label={this.props.labels.client} 
                                    context={this} 
                                    valKey={"clientName"} 
                                    value={this.props.serialized.clientName} 
                                 />
                            </div>
                            <div className="col-md-6">
                                <Input
                                    disabled={true}
                                    label={this.props.labels.application_name}
                                    context={this}
                                    valKey={"designName"}
                                    value={this.props.serialized.designName}
                                    valRules={Utility.getApplicationInputValRules(this.props.serialized.hasOwnProperty("branching"))}
                                />
                            </div>
                            <div className="col-md-6">
                                <div className="form-component-container">
                                    <label>
                                        Design Version
                                </label>
                                    <input
                                        type="text"
                                        id="input-design-version"
                                        disabled={!this.state.saveAsNewVersion}
                                        value={this.state.designVersion}
                                        onBlur={this.validateInputRepoVersion}
                                        onChange={this.handleNewDesignVersion}
                                        className="form-component"
                                    />
                                    <div className="form-error-tip error" id="error-input-design-version" style={{ display: 'none' }}>
                                        {DESIGN_VERSION_ERROR_MESSAGE}
                                    </div>
                                </div>
                            </div>
                            {!isInteractionsCommon &&
                                <div className="col-md-3">
                                    <label style={{marginTop: "4px",marginBottom: "-4px"}} 
                                            data-tip="The corresponding Java project version for Process Engine
                                                    to use for iAD / Workflow Tool dependencies">Dependency Version<i className="fe icon-info" /></label>
                                    <Input
                                        id="dependencyVersion"
                                        context={this}
                                        disabled={!isDesigner}
                                        valKey={"applicationVersion"}
                                        value={this.props.serialized.applicationVersion}
                                        valRules={{ "noEmpty": true }}
                                    />
                                </div>
                            }
                            <div className="col-md-3">
                                <div className="form-component-container">
                                    <label data-tip="The schema version used to validate export">JSON Schema Version<i className="fe icon-info" /></label>
                                    <input
                                        type="text"
                                        id="json-schema-version"
                                        disabled={true}
                                        value="1.1.0-SNAPSHOT"
                                        className="form-component"
                                    />
                                    <div className="form-error-tip error" id="error-input-design-version" style={{ display: 'none' }}>
                                        {DESIGN_VERSION_ERROR_MESSAGE}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {(!isDesigner) ? null : (
                            <div className="row">
                                <div className="col-md-12">
                                    <input
                                        id="checkbox-save-new-version"
                                        type="checkbox"
                                        checked={this.state.saveAsNewVersion}
                                        onChange={this.handleSaveAsNewVersion}
                                        />
                                    <label style={{ fontWeight: 'normal', padding: '0 .5em' }}>Create new version</label>
                                </div>
                            </div>
                        )}
                        {(!isDesigner) ? null : (
                            <div className="row ">
                                <div className="col-md-6" style={{paddingTop: "12px"}}>
                                    <div style={{width: "300px"}}>
                                        <label>Branching &amp; Merging</label>
                                        <BranchingMergingUpgrade 
                                            serialized={this.props.serialized}
                                            openSaveDialog={this.saveDesign}
                                            getLatestApplication={this.getLatestApplication} 
                                            isSaveNewVersionChecked={this.state.saveAsNewVersion}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6" style={{ display: 'flex', alignItems: 'flex-end'}}>
                                    <span style={{width: "310px", paddingRight: "20px"}} />
                                    <div>
                                        <div style={{display:'inline-flex'}}>
                                            <Button label="Ready for Release" 
                                                style={{
                                                    width: "max-content",
                                                    marginRight: "20px"
                                                }}
                                                disabled={this.saveButtonDisabled()}
                                                callback={this.onSaveRelease}
                                            />
                                            <Button
                                                label={(this.state.isSaving) ? 'Saving...' : 'Save'}
                                                disabled={this.saveButtonDisabled()}
                                                callback={() => {
                                                    const validateOnSave = this.state.validateOnSave;
                                                    const isCommonModule = Utility.getIsCommonModule(this.props.serialized);
                                                    const globalExceptionHandling = this.validateGlobalExceptionHandling(this.props.serialized)

                                                    // close any opened toast messages
                                                    $(".toast-error").click();

                                                    if (this.state.designVersionHasErrors) {
                                                        alert(`${DESIGN_VERSION_ERROR_TITLE}\n${DESIGN_VERSION_ERROR_MESSAGE}`);
                                                        return;
                                                    } else if (!isCommonModule && globalExceptionHandling) {
                                                        window.apm.notification.post("error", globalExceptionHandling +" in Exception Handling is empty",false, true);
                                                        return;
                                                    }

                                                    this.setState({ isSaving: true });
                                                    // toggleValidateOnSave checkbox controls ${this.state.validateOnSave}
                                                    if (!validateOnSave) {
                                                        // user saves from save dialog
                                                        this.saveDesign();
                                                    } else {
                                                        this.getValidationDialog();
                                                    }
                                                }}
                                                type="primary"
                                            />
                                        </div>
                                        
                                        {this.getToggleValidateCheckbox()}
                                    
                                    </div>
                                </div>
                            </div>
                        )}

                        <hr />

                        <ExportWidget>
                            <span>
                                <label>See Prompts Statuses</label>
                                <Button
                                    callback={() => {
                                        this.setState({ isPromptStatusesOpen: true });
                                    }}
                                    label="Prompt Statuses"
                                    labelIcon="message-square"
                                />
                                <WorkflowPromptStatuses
                                    isOpen={isPromptStatusesOpen}
                                    onComplete={() => {
                                        this.setState({ isPromptStatusesOpen: false });
                                    }}
                                />
                            </span>
                            {(!isDesigner) ? null : (
                                <span>
                                    <label>Submit All Prompts for Recording</label>
                                    <Button
                                        callback={() => {
                                            this.setState({ isSubmitPromptsOpen: true });
                                        }}
                                        label="Submit Prompts"
                                        labelIcon="mic"
                                    />
                                    <SubmitPrompts
                                        isOpen={isSubmitPromptsOpen}
                                        onComplete={() => {
                                            this.setState({ isSubmitPromptsOpen: false });
                                        }}
                                    />
                                </span>
                            )}
                        </ExportWidget>

                        <hr />

                        <div className="changelog-header">
                            <div>
                                <h4>
                                    Change Logs
                                    <a onClick={this.downloadChangeLogMaps} >
                                        <i className={"fe icon-download"} style={{ paddingLeft: '10px' }} />
                                    </a>
                                </h4>
                            </div>
                            <div>
                                <Button label="Compare Versions"
                                    callback={() => {
                                        const popupTitle = `<div>Compare Versions: ${this.props.serialized.clientName} ${this.props.serialized.designName}</div>`;
                                        window.apm.popup(popupTitle,
                                            '<div class="review_container"></div>',
                                            `<button class="w2ui-btn primary close-button">${window.l.popup.done}</button> `,
                                            [
                                                ".review_container",
                                                () => {
                                                    this.reviewDialog = ReactDOM.render(
                                                        <ReviewContainer />,
                                                        $(".review_container").get(0)
                                                    );
                                                    $(".w2ui-popup").find(".close-button").on("click", () => {
                                                        this.reviewDialog = ReactDOM.unmountComponentAtNode($(".review_container").get(0));
                                                        w2popup.close();
                                                    });
                                                }
                                            ],
                                            700, // height
                                            null, // node
                                            null, // treenode
                                            false // showClose
                                        )
                                    }} />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-md-12 changelog-container" id="changelogs-container" />
                        </div>

                    </div>
                </div>
            </div>
        );
    }

    _onDesignRolesChange(designRoles) {
        this.setState({ designRoles });
    }
}
